import React, { useState, useCallback, useMemo, useRef, useEffect, useReducer } from 'react'
import { MultiView, Item } from 'devextreme-react/multi-view'
import { useTranslation } from 'react-i18next'

import DropDownBox, { DropDownBoxRef } from 'devextreme-react/drop-down-box'
import { Button } from 'devextreme-react/button'
import { IPopupOptions } from 'devextreme-react/popup'

import { useFormikContext, useField } from 'formik'

import { BaseForm } from 'components/base-form'
import { FormErrors } from 'components/forms/form-errors'
import { IxLookupProvider, useIxLookupContext } from 'contexts/lookup-context'

import { IxAccount } from './account'

import { useSelector } from 'react-redux'
import { useAppDispatch } from 'store'
import { RootState } from 'slices'

import { IxObjectLookup } from 'components/forms/lookups/object-lookup.gen/object-lookup.gen'
import { getSettings, setSettingData, setSettings, setforceMainFilterTypeThunk, deleteforceMainFilterTypeThunk } from 'slices/account-lookup-slice'
import LoadIndicator from 'devextreme-react/load-indicator'
import useResizeObserver from '@react-hook/resize-observer'

import { AccountType } from 'helpers/account-type-enum'
import { isEqual } from 'lodash'
import { useLabelWidth } from 'hooks/use-label-width'
import client from 'api'

import { lookupDataSelector, setLookupData } from 'slices/lookup-data-slice'

import { Settings } from 'helpers/settings-enum'
import { settingsMenuSelector } from 'slices/ui'

import './account-lookup.scss'

const regExBuKrNr = /((?<BuKrNr>\d{1,6})\+)?/
const regExAccontNr = /(?<AccountNr>\d{1,10})/
const regExAccontNrWithStars = /(?<AccountNr>((\d{0,10}\*\*\d{1,10})|(\d{1,10}\*\*\d{0,10})))/
const regExAccontNrWithDots = /(?<AccountNr>((\d{1,10}\.))*\d{1,10})/
// eslint-disable-next-line
const regExAccontName = /(?<AccountName>[0-9,a-z,A-Z,äÄöÖüÜß#$§!%&?\- ]{0,100}[0-9,a-z,A-Z,äÄöÖüÜß#$§!%&?\-\.\*/ ]{0,100}[a-z,A-Z,äÄöÖüÜß#$§!%&?\- ]{1,1}[0-9,a-z,A-Z,äÄöÖüÜß#$§!%&?\-\.\*/ ]{0,100}[0-9,a-z,A-Z,äÄöÖüÜß#$§!%&?\- ]{0,1})?/
// eslint-disable-next-line
const regExPersonAccountNrWithSubaccount = /(?<AccountNr>\d{1,10}(\-\d{1,10})?((\.\d{0,10})))/
// eslint-disable-next-line
const regExPersonAccountNrPointOptional=/(?<AccountNr>\d{1,10}(\-\d{1,10})?)/

const regExPerson1 = new RegExp('^' + regExBuKrNr.source + regExPersonAccountNrWithSubaccount.source + '$')
const regExPerson2 = new RegExp('^' + regExBuKrNr.source + regExPersonAccountNrPointOptional.source + '$')
const regExPersonName1 = new RegExp('^' + regExBuKrNr.source + /\./.source + regExAccontName.source + '$')
const regExPersonName2 = new RegExp('^' + regExBuKrNr.source + regExAccontName.source + /\./.source + '$')

const regExSupplier1 = new RegExp('^' + regExBuKrNr.source + /\*/.source + regExAccontNr.source + '$')
const regExSupplier2 = new RegExp('^' + regExBuKrNr.source + regExAccontNr.source + /\*/.source + '$')
const regExSupplierName1 = new RegExp('^' + regExBuKrNr.source + /\*/.source + regExAccontName.source + '$')
const regExSupplierName2 = new RegExp('^' + regExBuKrNr.source + regExAccontName.source + /\*/.source + '$')

const regExCostCenter = new RegExp('^' + regExBuKrNr.source + /(?<AccountNr>\d{1,20}\/.{1,20})/.source + '$')

const regExAnlage = new RegExp('^' + regExBuKrNr.source + /(?<AccountNr>.{1,30}\.(SR|HR|[SKH]))/.source + '$')

const regExLedger1 = new RegExp('^' + regExBuKrNr.source + regExAccontNr.source + '$')
const regExLedger2 = new RegExp('^' + regExBuKrNr.source + regExAccontNrWithStars.source + '$')
const regExLedgerName = new RegExp('^' + regExBuKrNr.source + regExAccontName.source + '$')
// eslint-disable-next-line
const regExProject = new RegExp('^' + regExBuKrNr.source + /\#/.source + regExAccontNrWithDots.source + '$')
// eslint-disable-next-line
const regExProjectName = new RegExp('^' + regExBuKrNr.source + /\#/.source + regExAccontName.source + '$')

const matchAllowedAccount = new RegExp('^' + regExBuKrNr.source + /(?<AccountValue>[^\n]+)/.source + '$')

const initialState: any = {
    [AccountType.PersonAccount]: {url: '', data: []},
    [AccountType.LedgerAccount]: {url: '', data: []},
    [AccountType.SupplierAccount]: {url: '', data: []},
    [AccountType.ProjectAccount]: {url: '', data: []},
    [AccountType.CostCenterAccount]: {url: '', data: []},
    [AccountType.AnlageAccount]: {url: '', data: []},
}

const loadDataReducer = (state: any, action: any) => {
    return state = {...state, [action.name]: {url: action.url, data: action.data}}
}

export const useIxAccountLookupData = (
    active=true,
    __: any = '',
    {
        allowedAccountTypes,
        bukreisNr,
        bukreisId
    }: any={}
) => {
    // const [data, setData] = useState<any>([])
    const [lookupMap, setLookupMap] = useState<any>([])
    const idsMap: any = useRef<any[]>([])
    const {value: number} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupNumberFormat))
    const {value: separator} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupSeparatorFormat))
    const {value: info} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupInfoFormat))

    const params = useMemo(() => {
        let accountsTypes = Object.values(AccountType).filter(account => typeof account === 'number' && account !== 5 && account !== 6 && account !== 8)
        if (allowedAccountTypes && allowedAccountTypes.length) {
            accountsTypes = accountsTypes.filter((account) => allowedAccountTypes.includes(account))
        }

        return { 'accTypes': accountsTypes.sort().join(','), bukreisNr: bukreisNr || '', bukreisId: bukreisId || '' }
    }, [allowedAccountTypes, bukreisNr, bukreisId])

    const dispatch = useAppDispatch()
    const { status, data } = useSelector((state: RootState) => lookupDataSelector(
        state,
        'accountLookup',
        params
    ))

    const getFormat = useCallback( (accountId: any) => {
        const item = lookupMap.find((x: any) => {
            return x.id == accountId  // eslint-disable-line
        })

        if (item) { return item.value }

        else if (!idsMap.current.find((id: any) => id === accountId)) {
            idsMap.current = [...idsMap.current, accountId]
            client.get(`/api/ixweb/konto/?hdr_id=${accountId}&action=eql`).then((e: any) => {
                const account = e.data[0]
                setLookupMap([...lookupMap, {id: accountId, value: `${number.prefix}${account.BUKREISID}+${account.KONTONUMMER}${number.suffix}${separator.value}${info.prefix}${account.KONTONAME}${info.suffix}`}])
            })
        }

        else { return ''}

    }, [lookupMap, info, number, separator])

    const getAccountsData = useCallback( async(force=false) => {
        if (status === 'loading' || (status === 'loaded' && !force)) return

        let accountMap: any[] = []
        let accountsTypes = Object.values(AccountType).filter(account => typeof account === 'number' && account !== 5 && account !== 6 && account !== 8)
        if (allowedAccountTypes && allowedAccountTypes.length) {
            accountsTypes = accountsTypes.filter((account) => allowedAccountTypes.includes(account))
        }

        const params = { 'accTypes': accountsTypes.sort().join(','), bukreisNr: bukreisNr || '', bukreisId: bukreisId || '' }
        dispatch(setLookupData({ lookupName: 'accountLookup', params, status: 'loading', data: [] }))

        if (bukreisNr) {
            for (const account of accountsTypes){
                const res = await client.get(`/api/ixweb/accounts/${account}/?bukreisnr=${bukreisNr}`)
                accountMap = [...accountMap,...res.data]
            }
        }

        if (bukreisId) {
            for (const account of accountsTypes){
                const res = await client.get(`/api/ixweb/accounts/${account}/?bukreisid=${bukreisId}`)
                accountMap = [...accountMap,...res.data]
            }
        }

        dispatch(setLookupData({lookupName: 'accountLookup', params, status: 'loaded', data: accountMap}))
    },[dispatch, allowedAccountTypes, bukreisNr, bukreisId, status])


    const format = useCallback((x: any) => {
        return `${number.prefix}${x.BuKrNr}+${x.AccountNr}${number.suffix}${separator.value}${info.prefix}${x.AccountName}${info.suffix}`
    }, [number, separator, info])

    useEffect(() => {
        if (data.length) {
            const typesMap = data.map((t: any) => ({
                id: t.AccountId,
                value: format(t),
            }))
            setLookupMap(typesMap)
        }
    }, [format, data])

    useEffect(() => {
        active && getAccountsData()
    },[active, getAccountsData])

    const getLookupValue = useCallback((accountId: any) => {
        if (data) {
            const item = data.find((x: any) => {
                return x.AccountId == accountId  // eslint-disable-line
            })

            return item
        }
    }, [data])

    return { lookupMap, getFormat, loadData: getAccountsData, getLookupValue }
}

export const AccountLookup = ({
    allowedAccountTypes=[],
    disableAccountType=false,
    disableObject=false,
    disableMainFilterType=false,
    removeMainFilterType=false,
    visibleMainFilterType=true,
    name,
    label,
    enableMultiselect = false,
    forceMainFilterType = null,
    showOnlyMainAccounts,
    currentGVC,
    mainBuKreisNr,
    bukreisnr,
    accountType,
    fibuNr = 0,
    onSelectionChanged,
    objectFieldName,
    multiselect=false,
    rangeWithComma=false,
    rangeMode=false
    }: any) => {
    const {value: number} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupNumberFormat))
    const {value: separator} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupSeparatorFormat))
    const {value: info} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupInfoFormat))
    const { settings, getStatus } = useSelector((state: RootState) => state.accountLookupSlice)
    const [selectedRow, setSelectedRow] = useState<any>([])
    const [field, meta] = useField(name);
    const { setFieldValue, setFieldTouched } = useFormikContext();
    const [open, setOpen] = useState(false)
    const [searchText, setSearchText] = useState('')
    const [searchBuKrNr, setSearchBuKrNr] = useState<any>(null)
    const [inFocus, setInFocus] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [currentActiveButton, setCurrentActiveButton] = useState<any>()
    const [callback, setCallback] = useState<any>(null)
    const [accountsData, dataDispatch] = useReducer(loadDataReducer, initialState)
    const [existBuKrNr, setExistBuKrNr] = useState<any>(null)
    const [existRow, setExistRow] = useState<any>(null)
    const [isLoadingAccount, setIsLoadingAccount] = useState<any>({
        [AccountType.PersonAccount]: false,
        [AccountType.LedgerAccount]: false,
        [AccountType.SupplierAccount]: false,
        [AccountType.ProjectAccount]: false,
        [AccountType.CostCenterAccount]: false,
        [AccountType.AnlageAccount]: false
    })

    const dispatch = useAppDispatch()
    const dropdownbox = useRef<DropDownBoxRef>(null)
    const ref = useRef(null)
    const { t } = useTranslation()
    const [reportObjValue, setReportObjValue] = useState<any>(null)
    const { getFieldValue, addField, setFieldValue: setLookupFieldValue } = useIxLookupContext()

    const [gridRef, setGridRef] = useState<any>(null)
    const [fieldWidth, setFieldWidth] = useState<any>(null)
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    const {value: openLookupOnClick} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.openLookupOnClick))
    const getLabelWidth = useLabelWidth(formFieldType)

    const isFloatingLabel = useMemo(() => {
        return formFieldType === 'floating-label' || formFieldType === 'floating-label-compact'
    },[formFieldType])

    const logWidth = useCallback(() => {
        const refDropdownbox: any = dropdownbox.current
        const input: any = refDropdownbox?.instance()?.element().querySelector('.dx-texteditor-input-container')

        input && setFieldWidth(input.getBoundingClientRect().width)
    },[])

    const onResize = useCallback((e: any) => {
        isFloatingLabel && setFieldWidth(e.contentRect.width)
    },[isFloatingLabel])

    useResizeObserver((dropdownbox.current?.instance()?.element().querySelector('.dx-texteditor-input-container') as any), onResize)

    useEffect(() => {
        isFloatingLabel && logWidth()
    },[logWidth, field.value, isFloatingLabel])

    useEffect(() => {
        if (isFloatingLabel) {
            const refDropdownbox: any = dropdownbox.current
            const floatingLabel: HTMLElement = refDropdownbox?.instance()?.element().querySelector('.dx-label')
            const input: any = refDropdownbox?.instance()?.element().querySelector('input.dx-texteditor-input')

            if (field.value && !isLoading) {
                const focusedFloatingLabel: HTMLElement = refDropdownbox?.instance()?.element().querySelector('.dx-texteditor-label .dx-label')

                floatingLabel && focusedFloatingLabel && (focusedFloatingLabel.style.left = `calc(100% - 60px - ${floatingLabel.offsetWidth-12}px)`)

                if (floatingLabel) {
                    const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                    const floatingLabelWidth = getLabelWidth(label, fontFamily)

                    fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+26)}px`)
                }

            }
            else if (!field.value) {
                if (floatingLabel) {
                    const fontFamily = window.getComputedStyle(floatingLabel).fontFamily
                    const floatingLabelWidth = getLabelWidth(label, fontFamily)

                    floatingLabel.style.left = inFocus || open ? `calc(100% - 32px - ${floatingLabel.offsetWidth-12}px)` : '0px'
                    fieldWidth && (input.style.width = `${fieldWidth - (floatingLabelWidth+16)}px`)
                }

            }
        }

    },[field.value, getLabelWidth, label, fieldWidth, isLoading, inFocus, open, isFloatingLabel])

    useEffect(() => {
        addField(name, null)

    },[addField, getFieldValue, name])

    useEffect(() => {
        if (objectFieldName) {
            const value = getFieldValue(objectFieldName)
            if (isEqual(value, reportObjValue)) {return}
            else { setReportObjValue(value) }
        }

    },[getFieldValue, setReportObjValue, objectFieldName, reportObjValue])


    useEffect(() => {
        reportObjValue && client.get(`/api/ixweb/xsp_splitobjrangegrp_proc/?ObjRange=${typeof reportObjValue === 'string' ? reportObjValue : Array.isArray(reportObjValue) ? reportObjValue[0]?.OBJNR : reportObjValue?.BEZ}&userid=38`)
        .then((e) => {
            setExistBuKrNr(e.data[0]?.OBJNR)
            setSearchBuKrNr(e.data[0]?.OBJNR)
        })

    },[reportObjValue])

    useEffect(() => {
        isLoading && sessionStorage.setItem('fieldValue', '')
    },[isLoading])

    const onAllowedAccountTypes = useCallback((allowedAccountTypes: any, accountTypeValue: any) => {
        if (allowedAccountTypes.length > 0 && !allowedAccountTypes.includes(AccountType.LedgerAccount) && !allowedAccountTypes.includes(accountTypeValue)) {
            return allowedAccountTypes.sort()[0]
        }
        if (allowedAccountTypes.length > 0 && allowedAccountTypes.includes(accountTypeValue)) {
            return accountTypeValue
        }
        if (allowedAccountTypes.includes(AccountType.LedgerAccount)) {
            return AccountType.LedgerAccount
        }
        else {
            return accountTypeValue
        }
    },[])

    const activeButton = useMemo(() => (onAllowedAccountTypes(allowedAccountTypes, settings.accountType)),[onAllowedAccountTypes, settings.accountType, allowedAccountTypes])

    const accountTypes = useMemo(() => ([
        {id: AccountType.PersonAccount, label: 'global:label.accountType.personAccount'},
        {id: AccountType.LedgerAccount, label: 'global:label.accountType.ledgerAccount'},
        {id: AccountType.SupplierAccount, label: 'global:label.accountType.supplierAccount'},
        {id: AccountType.ProjectAccount, label: 'global:label.accountType.projectAccount'},
        {id: AccountType.CostCenterAccount, label: 'global:label.accountType.costCenterAccount'},
        {id: AccountType.AnlageAccount, label: 'global:label.accountType.anlageAccount'}
    ]),[])

    useEffect(() => {
        (!rangeMode && !open) && setSelectedRow(existRow)
    },[field.value, open, existRow, rangeMode])

    useEffect(() => {
        forceMainFilterType && dispatch(setforceMainFilterTypeThunk({[name]: {
            [AccountType.PersonAccount]: forceMainFilterType,
            [AccountType.LedgerAccount]: forceMainFilterType,
            [AccountType.SupplierAccount]: forceMainFilterType,
            [AccountType.ProjectAccount]: forceMainFilterType,
            [AccountType.CostCenterAccount]: forceMainFilterType,
            [AccountType.AnlageAccount]: forceMainFilterType
        }}))

        return () => {
            forceMainFilterType && dispatch(deleteforceMainFilterTypeThunk(name))
        }
    },[dispatch, forceMainFilterType, name])

    useEffect(() => {
        if (getStatus !== 'fulfilled' && getStatus !== 'pending') {
            accountType >= 0 && dispatch(setSettings({accountType: accountType}))
            dispatch(getSettings())
        }

    },[dispatch, getStatus, allowedAccountTypes, forceMainFilterType, accountType, settings.forceMainFilterType, name])

    const onActiveButton = useCallback((e: any) => {
        dispatch(setSettingData({typeSetting: 'accountType', value: Number(e.element.id)}))

    },[dispatch])

    const handleRowDblClick = useCallback((e: any) => {
        if (rangeMode) {
            if (multiselect) {
                const selectedRowsNr = gridRef.current?.instance()?.getSelectedRowsData().map((row: any) => row.AccountNr)
                const fieldValue = (document.querySelectorAll(`[name=${name}]`)[1] as any).value
                setFieldValue(
                    field.name,
                    `${fieldValue}${(!fieldValue.endsWith('-') && fieldValue ) ? ',' : ''}${selectedRowsNr?.length ? `${selectedRowsNr}` : ''}${!selectedRowsNr.includes(e.data.AccountNr) ? selectedRowsNr?.length ? `,${e.data.AccountNr}` : e.data.AccountNr : ''}`,
                    true
                )
            }
            else {
                setFieldValue(field.name, e.data.AccountNr, true)
                setExistRow(e.data)
             }
        }
        else {
            onSelectionChanged && onSelectionChanged(e.data)
            setFieldValue(field.name, !rangeMode ? e.data.AccountId : e.data.AccountNr, true)
            setLookupFieldValue(name, e.data)
            setExistRow(e.data)
        }
        dropdownbox.current?.instance()?.close()
        setInFocus(false)

    }, [gridRef, field.name, setFieldValue,onSelectionChanged, setInFocus, setLookupFieldValue, name, rangeMode, multiselect])

    useEffect(() => {
        (field.value && field.value !== existRow?.AccountId && !rangeMode) && client.get(`/api/ixweb/fnc_GetAccountInfo/?kontoid=${field.value}`).then((res: any) => {
            const data = {
                AccountId: res.data.KONTOID,
                BuKrNr: res.data.BUKRNR,
                AccountName: res.data.KONTONAME,
                ParentNr: res.data.PARENTNR,
                AccountNr: res.data.KONTONR,
                AdditionalInfo1: res.data.ADDITIONALINFO1,
                AdditionalInfo2: res.data.ADDITIONALINFO2,
                AdditionalInfo3: res.data.ADDITIONALINFO3,
                AdditionalInfo4: res.data.ADDITIONALINFO4,
                AdditionalInfo5: res.data.ADDITIONALINFO5,
            } as any
            handleRowDblClick({data: data})
            setSearchBuKrNr(data.BuKrNr)
        })
    },[field.value, existRow, rangeMode])

    const onOkClick = useCallback((e: any) => {
        const rowData = e.singleVisibleRowData || selectedRow

        if (rangeMode) {
            const selectedRows = rowData ? [rowData] : gridRef.current?.instance()?.getSelectedRowsData()
            const selectedRowsNr = selectedRows.map((row: any) => row['AccountNr'])

            if (multiselect) {
                const fieldValue = (document.querySelectorAll(`[name=${name}]`)[1] as any).value
                setFieldValue(field.name, `${fieldValue}${(!fieldValue.endsWith('-') && fieldValue && selectedRowsNr.length) ? ',' : ''}${selectedRowsNr}`, true)
            }
            else {
                selectedRows.length && setExistRow(selectedRows[0])
                selectedRowsNr.length && setFieldValue(field.name, selectedRowsNr, true)
            }
        }
        else {
            if (rowData) {
                onSelectionChanged && onSelectionChanged(rowData)
                setFieldValue(field.name, !rangeMode ? rowData.AccountId : rowData.AccountNr, true)
                setLookupFieldValue(name, rowData)
                setExistRow(rowData)
            }
        }

        dropdownbox.current?.instance()?.close()
        setInFocus(false)
    }, [gridRef, selectedRow, field, setFieldValue, onSelectionChanged, setInFocus, setLookupFieldValue, name, rangeMode, multiselect])

    const onRowClick = useCallback((e: any) => {
        if (e.data) {setSelectedRow(e.data)}
    },[])

    const handlePopupHiding = useCallback((e: any) => {
        if (rangeMode) {
            gridRef.current?.instance()?.clearSelection()
        }
        else {
            sessionStorage.setItem('singleVisibleRow', '')
            existBuKrNr !== Number(searchBuKrNr) && setSearchBuKrNr(searchBuKrNr || existBuKrNr)
        }
        e.event && setInFocus(true)
        e.event && setTimeout(() => {dropdownbox.current?.instance()?.focus()}, 0)
        dropdownbox.current?.instance()?.close()
        gridRef.current?.instance()?.clearFilter()
        setSearchText('')
    }, [existBuKrNr, searchBuKrNr, rangeMode, gridRef])

    const handlePopupShowing = useCallback((e: any) => {
        setInFocus(false)
        e.component.content().parentNode.classList.add('dropdownbox-input-overlay')
    }, [])

    const [isPopupResponsive, setIsPopupResponsive] = useState<boolean>(false)
    const [popupSize, setPopupWidth] = useState<any>({width: 1050, height: 750})

    const handleResize = useCallback((e: any) => {
        setIsPopupResponsive(matchMedia(`(max-width: ${popupSize.width}px)`).matches)
    },[popupSize])

    useEffect(() => {
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [handleResize])

    const dropDownOptions: IPopupOptions = useMemo(() => ({
        minWidth: '176px',
        width: isPopupResponsive ? '100vw' : `${popupSize.width}px`,
        height: popupSize.height,
        minHeight: '550px',
        dragEnabled: true,
        position: open ? { at: 'left bottom', my: 'left top', collision: 'flipfit', of: dropdownbox.current?.instance()?.field() } : {},
        resizeEnabled: true,
        onResizeEnd: (e) => {setPopupWidth({width: e.width, height: e.height} )},
        showCloseButton: false,
        onShowing: handlePopupShowing,
        onHiding: handlePopupHiding,
        title: 'ACCOUNT',
        showTitle: true,
        disabled: false,
        hideOnOutsideClick: false,
        hideOnParentScroll: false,
        toolbarItems: [
            {
                toolbar: 'top',
                location: 'after',
                widget: 'dxButton',
                disabled: false,
                options: {
                    type: 'close',
                    icon: 'close',
                    onClick: () => {
                        dropdownbox.current?.instance()?.close()
                        setTimeout(() => {dropdownbox.current?.instance()?.focus()}, 0)
                    }
                }
            }
        ],
    }), [handlePopupShowing, handlePopupHiding, isPopupResponsive, popupSize, open])

    const formik = useMemo(() => ({
        initialValues: {
            OBJNR: searchBuKrNr
        },
        enableReinitialize: true,
        jsonSchema: null,
        avjOptions: {
            coerceTypes: true,
            $data: true,
        },
        validateOnBlur: false,

    }),[searchBuKrNr])

    useEffect(() => {
        if (open) {
            sessionStorage.setItem('fieldValue', '')
            bukreisnr && setSearchBuKrNr(bukreisnr)
            setCallback(() => {
                return (_: string, value: any) => {
                    setExistBuKrNr(value?.OBJNR)
                }
            })
        }
    },[open, bukreisnr])

    const onFieldValueNotFound = useCallback(() => {
        dropdownbox.current?.instance()?.open()
    }, [])

    const setData = useCallback( async(bukrNr: number | null, account: any) => {
        const forceMainFilterType = settings.forceMainFilterType[name] && settings.forceMainFilterType[name][account]
        const mainFilterType = settings.mainFilterType[account] || 0
        const url = `/api/ixweb/accounts/${account}/?MainFilterType=${removeMainFilterType ? 0 : (forceMainFilterType || mainFilterType)}&bukreisnr=${bukrNr || existBuKrNr}`
        if (!existBuKrNr) {
            dataDispatch({name: account, data: [], url: url})
            setIsLoadingAccount({...isLoadingAccount, [account]: false})
        }

        if ((open ? url !== accountsData[account].url : true) && (bukrNr || existBuKrNr) && !isLoading) {
            setIsLoadingAccount({...isLoadingAccount, [account]: true})
            dataDispatch({name: account, data: [], url: url})

            const res = await client.get(url)

            dataDispatch({name: account, data: res.data, url: url})
            setIsLoadingAccount({...isLoadingAccount, [account]: false})

            return res
        }
        return

    },[accountsData, existBuKrNr, settings.mainFilterType, open, isLoadingAccount, isLoading, settings.forceMainFilterType, name, removeMainFilterType])

    useEffect(() => {
        setData(null, accountType || activeButton)
    },[settings.mainFilterType, activeButton, existBuKrNr, accountType, settings.forceMainFilterType])


    const _onSelectionChanged = useCallback((e: any) => {
        e.selectedRowsData.length && setSelectedRow(e.selectedRowsData[0])
    }, [])

    const handleGridReady = useCallback((e: any, grid: any) => {
        const visibleRows = e.component.getVisibleRows()

        if (visibleRows.length === 1) {
            const rowData = visibleRows[0]?.data

            grid.current?.instance()?.selectRows(rowData?.AccountId, true)
            !rangeMode && setSelectedRow(rowData)

            if (!open) {
                onOkClick({singleVisibleRowData: rowData})
                grid.current?.instance()?.clearFilter()
                setSearchText('')
            }

        }
        else {
            if (searchText) {
                setOpen(true)
            }
        }
    },[rangeMode, onOkClick, open, searchText])

    const contentRender = useCallback((_: any) => {
        return (
            <BaseForm {...formik}>
                {
                    ({values}: any) => (
                        <div style={{display: 'inline'}} ref={ref}>

                            <IxObjectLookup
                                name='OBJNR'
                                readOnly={disableObject}
                                fibuNr={fibuNr}
                            />
                            {
                                disableAccountType
                                ?   <div className='account-btns'>
                                        {
                                            accountType >= 0
                                                ? accountTypes.map((type: any) => {
                                                    return type.id === accountType && <Button text={t(type.label)} type='success' id={type.id} onClick={onActiveButton} key={type.id} hint={t(type.label)}/>
                                                })
                                                : <Button text={t('global:label.accountType.ledgerAccount')} type='success' id='1' onClick={onActiveButton} hint={t('global:label.accountType.ledgerAccount')}/>
                                        }

                                    </div>
                                :   <div className='account-btns'>
                                        {
                                            accountTypes.map((account: any) => (
                                                (allowedAccountTypes.length === 0 || allowedAccountTypes.includes(account.id)) &&
                                                    <Button text={t(account.label)} type={activeButton === account.id ? 'success' : 'normal'} id={account.id} onClick={onActiveButton} key={account.id} hint={t(account.label)}/>
                                            ))
                                        }
                                    </div>
                            }

                            <MultiView
                                selectedIndex={
                                    disableAccountType ?
                                        accountType >= 0 ? accountType :  AccountType.LedgerAccount - 1
                                    : activeButton-1
                                    }

                                swipeEnabled={false}
                                focusStateEnabled={false}
                                deferRendering={true}
                                height='calc(100% - 130px)'
                            >

                                <Item>
                                    <IxAccount
                                        handleRowDblClick={handleRowDblClick}
                                        onRowClick={onRowClick}
                                        searchText={searchText.includes('.') || currentActiveButton === AccountType.PersonAccount ? searchText : ''}
                                        selectedRowKey={selectedRow}
                                        data={accountsData[AccountType.PersonAccount].data}
                                        isLoadingAccount={isLoadingAccount[AccountType.PersonAccount]}
                                        multiselect={multiselect}
                                        onSelectionChanged={_onSelectionChanged}
                                        setGridRef={setGridRef}
                                        rangeMode={rangeMode}
                                        handleGridReady={handleGridReady}
                                        accountType={AccountType.PersonAccount}
                                        dropdownbox={dropdownbox.current}
                                    />
                                </Item>

                                <Item>
                                    <IxAccount
                                        handleRowDblClick={handleRowDblClick}
                                        onRowClick={onRowClick}
                                        visibleMainFilterType={visibleMainFilterType}
                                        disableMainFilterType={disableMainFilterType}
                                        removeMainFilterType={removeMainFilterType}
                                        forceMainFilterType={forceMainFilterType}
                                        searchText={((Number(searchText) && !searchText.includes('.')) || searchText.includes('**')) || currentActiveButton === AccountType.LedgerAccount ? searchText : ''}
                                        selectedRowKey={selectedRow}
                                        data={accountsData[AccountType.LedgerAccount].data}
                                        isLoadingAccount={isLoadingAccount[AccountType.LedgerAccount]}
                                        name={name}
                                        multiselect={multiselect}
                                        onSelectionChanged={_onSelectionChanged}
                                        setGridRef={setGridRef}
                                        rangeMode={rangeMode}
                                        handleGridReady={handleGridReady}
                                        height={removeMainFilterType ? '100%' : '90.3%'}
                                        accountType={AccountType.LedgerAccount}
                                        dropdownbox={dropdownbox.current}
                                    />
                                </Item>


                                <Item>
                                    <IxAccount
                                        handleRowDblClick={handleRowDblClick}
                                        onRowClick={onRowClick}
                                        visibleMainFilterType={visibleMainFilterType}
                                        disableMainFilterType={disableMainFilterType}
                                        removeMainFilterType={removeMainFilterType}
                                        forceMainFilterType={forceMainFilterType}
                                        searchText={searchText.includes('*') || currentActiveButton === AccountType.SupplierAccount ? searchText : ''}
                                        height={removeMainFilterType ? '100%' : '90.3%'}
                                        selectedRowKey={selectedRow}
                                        data={accountsData[AccountType.SupplierAccount].data}
                                        isLoadingAccount={isLoadingAccount[AccountType.SupplierAccount]}
                                        name={name}
                                        multiselect={multiselect}
                                        onSelectionChanged={_onSelectionChanged}
                                        setGridRef={setGridRef}
                                        rangeMode={rangeMode}
                                        handleGridReady={handleGridReady}
                                        accountType={AccountType.SupplierAccount}
                                        dropdownbox={dropdownbox.current}
                                    />
                                </Item>

                                <Item>
                                    <IxAccount
                                        handleRowDblClick={handleRowDblClick}
                                        onRowClick={onRowClick}
                                        searchText={searchText.includes('#') || currentActiveButton === AccountType.ProjectAccount ? searchText : ''}
                                        selectedRowKey={selectedRow}
                                        data={accountsData[AccountType.ProjectAccount].data}
                                        isLoadingAccount={isLoadingAccount[AccountType.ProjectAccount]}
                                        multiselect={multiselect}
                                        onSelectionChanged={_onSelectionChanged}
                                        setGridRef={setGridRef}
                                        rangeMode={rangeMode}
                                        handleGridReady={handleGridReady}
                                        accountType={AccountType.ProjectAccount}
                                        dropdownbox={dropdownbox.current}
                                    />
                                </Item>

                                <Item>
                                    <IxAccount
                                        handleRowDblClick={handleRowDblClick}
                                        onRowClick={onRowClick}
                                        searchText={searchText.includes('/') && searchText.match(regExCostCenter) ? searchText : ''}
                                        selectedRowKey={selectedRow}
                                        data={accountsData[AccountType.CostCenterAccount].data}
                                        isLoadingAccount={isLoadingAccount[AccountType.CostCenterAccount]}
                                        multiselect={multiselect}
                                        onSelectionChanged={_onSelectionChanged}
                                        setGridRef={setGridRef}
                                        rangeMode={rangeMode}
                                        handleGridReady={handleGridReady}
                                        accountType={AccountType.CostCenterAccount}
                                        dropdownbox={dropdownbox.current}
                                    />
                                </Item>

                                <Item></Item>

                                <Item>
                                    <IxAccount
                                        handleRowDblClick={handleRowDblClick}
                                        onRowClick={onRowClick}
                                        searchText={
                                            (searchText.includes('S') || searchText.includes('K') || searchText.includes('H')) && searchText.match(regExAnlage) ? searchText : ''
                                        }
                                        selectedRowKey={selectedRow}
                                        data={accountsData[AccountType.AnlageAccount].data}
                                        isLoadingAccount={isLoadingAccount[AccountType.AnlageAccount]}
                                        multiselect={multiselect}
                                        onSelectionChanged={_onSelectionChanged}
                                        setGridRef={setGridRef}
                                        rangeMode={rangeMode}
                                        handleGridReady={handleGridReady}
                                        accountType={AccountType.AnlageAccount}
                                        dropdownbox={dropdownbox.current}
                                    />
                                </Item>

                            </MultiView>

                            <div className='confirm-btns'>
                                <Button text={t('global:button.ok')} type='default' onClick={onOkClick} />
                                <Button text={t('global:button.cancel')} type='default' onClick={handlePopupHiding} />
                            </div>
                        </div>
                    )
                }

            </BaseForm>

        )
    }, [onOkClick, handlePopupHiding, onActiveButton, activeButton, handleRowDblClick, onRowClick, t, formik, accountType, forceMainFilterType, name, isLoadingAccount, fibuNr, multiselect, handleGridReady, visibleMainFilterType,
        disableAccountType, disableMainFilterType, removeMainFilterType, disableObject, accountTypes, allowedAccountTypes, searchText, currentActiveButton, accountsData, selectedRow, _onSelectionChanged, rangeMode
        ])

    const handleFilter = useCallback(async (bukrNr: number, valueNr: string | undefined, valueName: string | undefined, account: any, allowedAccountValue=null) => {
        setExistBuKrNr(bukrNr)
        setIsLoading(true)
        if (valueNr || valueName) {
            const res = await setData(bukrNr, account)
            setIsLoading(false)
            const accountNrs = res?.data.filter((row: any) => row.AccountNr === valueNr)
            const accountNames = res?.data.filter((row: any) => row.AccountName === valueName)
            const rowData = (accountNrs?.length === 1 && accountNrs[0]) || (accountNames?.length === 1 && accountNames[0])
            if (rowData) {
                setFieldValue(name, !rangeMode ? rowData.AccountId : rowData.AccountNr, true)
                setLookupFieldValue(name, rowData)
                setSearchText('')
                setSelectedRow(rowData)
                setExistRow(rowData)
                onSelectionChanged && onSelectionChanged(rowData)
            } else {
                setSearchText(allowedAccountValue || (valueNr || valueName || ''))
            }
        } else {
            setIsLoading(false)
            setSearchText('')
            setOpen(true)
        }

    },[setFieldValue, setData, name, onSelectionChanged, setLookupFieldValue, rangeMode])

    const handleMatchRegEx = useCallback( async(account: any, bukreisnr=rangeMode && existBuKrNr ? existBuKrNr : 1, accountNr=undefined, accountName=undefined, allowedAccountValue=null) => {
        dispatch(setSettingData({typeSetting: 'accountType', value: account}))

        if (Number(bukreisnr) === existBuKrNr) {
            handleFilter(Number(bukreisnr), accountNr && accountNr, accountName, account, allowedAccountValue)
        } else {
            setCallback(() => {
                return (_: string, value: any) => {
                    value && handleFilter(value.OBJNR, accountNr && accountNr, accountName, account, allowedAccountValue)
                    setCallback(null)
                }
            })
        }

        setSearchBuKrNr(bukreisnr)
    },[dispatch, handleFilter, existBuKrNr, rangeMode])

    const allowedAccountMatching = useCallback((value: string) => {
        const matching: any = value.match(matchAllowedAccount)
        const {BuKrNr, AccountValue} = matching.groups
        let allowedAccountNr: any = !isNaN(AccountValue) ? `${AccountValue}` : null

        if (allowedAccountTypes[0] === AccountType.PersonAccount) {
            if ((!AccountValue.includes('.') && Number(AccountValue)) || AccountValue.match(regExPerson2)) {
                allowedAccountNr =`${AccountValue}.`
            }
            if (AccountValue.match(regExPerson1)) {
                allowedAccountNr = AccountValue
            }
        }

        if (allowedAccountTypes[0] === AccountType.LedgerAccount && AccountValue.includes('.')) {
            allowedAccountNr =  null
        }

        if (allowedAccountTypes[0] === AccountType.SupplierAccount) {
            let matching: any

            if ((matching = AccountValue.match(regExSupplier1) || AccountValue.match(regExSupplier2))) {
                const {AccountNr} = matching.groups
                allowedAccountNr = AccountNr
            }
            if (Number(AccountValue) && !AccountValue.endsWith('.') && !AccountValue.startsWith('.')) {
                allowedAccountNr = AccountValue
            }
            if (AccountValue.includes('.')) {
                allowedAccountNr = null
            }
        }

        if (allowedAccountTypes[0] === AccountType.ProjectAccount) {

            if (!AccountValue.includes('#') && AccountValue.match(regExAccontNrWithDots)) {
                const accountNr = AccountValue.match(regExAccontNrWithDots).groups.AccountNr
                allowedAccountNr = `#${accountNr}`
            }
        }

        setCurrentActiveButton(allowedAccountTypes[0])
        handleMatchRegEx(
            allowedAccountTypes[0],
            BuKrNr,
            allowedAccountNr,
            (!Number(allowedAccountNr) && !allowedAccountNr?.match(regExPerson1) && !allowedAccountNr?.match(regExPerson2)) && AccountValue,
            AccountValue
            )

    },[allowedAccountTypes, handleMatchRegEx])

    useEffect(() => {
        if (!open && existRow && existRow.BuKrNr !== existBuKrNr && !rangeMode) {
            setSearchBuKrNr(null)
            setTimeout(() => {
                setExistBuKrNr(existRow.BuKrNr)
                setSearchBuKrNr(existRow.BuKrNr)
            }, 100)
        }
        if (open && existBuKrNr && !existRow) {
            setExistBuKrNr(existBuKrNr)
            setSearchBuKrNr(existBuKrNr)
        }
    },[open, existRow, existBuKrNr, rangeMode])

    useEffect(() => {
        !field.value && setExistRow(null)
    },[field.value, gridRef])

    const handleChange = useCallback((e: any) => {
        !field.value && setSelectedRow(null)

        if (!e.event) { return }
        if (!e.value ) {
            setSelectedRow(null)
            setFieldValue(field.name, null, true)
            setLookupFieldValue(name, null)
            onSelectionChanged && onSelectionChanged(null)
            return
        }

        setInFocus(false)
        setFieldTouched(name, true)

        if (rangeMode) {
            if (!multiselect) {
                setExistRow(accountsData[activeButton].data.find((row: any) => row.AccountNr === e.value))
            }
            setFieldValue(field.name, e.value, true)
        }
        else {
            let matching;
            if (allowedAccountTypes.length === 1) {
                allowedAccountMatching(e.value)
                return
            }

            if ((matching = e.value.match(regExPerson1) || e.value.match(regExPersonName1) || e.value.match(regExPersonName2))) {
                const {BuKrNr, AccountNr, AccountName} = matching.groups
                setCurrentActiveButton(AccountType.PersonAccount)
                handleMatchRegEx(AccountType.PersonAccount, BuKrNr, AccountNr, AccountName)
                return
            }

            if ((matching = e.value.match(regExSupplier1) || e.value.match(regExSupplier2) || e.value.match(regExSupplierName1) || e.value.match(regExSupplierName2))) {
                const {BuKrNr, AccountNr, AccountName} = matching.groups
                setCurrentActiveButton(AccountType.SupplierAccount)
                handleMatchRegEx(AccountType.SupplierAccount, BuKrNr, AccountNr, AccountName)
                return
            }

            if ((matching = e.value.match(regExProject) || e.value.match(regExProjectName))) {
                const {BuKrNr, AccountNr, AccountName} = matching.groups
                setCurrentActiveButton(AccountType.ProjectAccount)
                handleMatchRegEx(AccountType.ProjectAccount, BuKrNr, AccountNr, AccountName)
                return
            }

            if ((matching = e.value.match(regExCostCenter) || e.value.match(/^\/$/))) {
                if (matching.groups) {
                    const {BuKrNr, AccountNr, AccountName} = matching.groups
                    handleMatchRegEx(AccountType.CostCenterAccount, BuKrNr, AccountNr, AccountName)
                } else {
                    handleMatchRegEx(AccountType.CostCenterAccount)
                }
                return
            }

            if ((matching = e.value.match(regExAnlage) || e.value.match(/^(SR|HR|[SKH])$/))) {
                if (matching.groups) {
                    const {BuKrNr, AccountNr, AccountName} = matching.groups
                    handleMatchRegEx(AccountType.AnlageAccount, BuKrNr, AccountNr, AccountName)
                } else {
                    handleMatchRegEx(AccountType.AnlageAccount)
                }
                return
            }

            // if ((matching = e.value.match(regExLedger1) || e.value.match(regExLedger2) || e.value.match(regExLedgerName))) {
            //     const {BuKrNr, AccountNr, AccountName} = matching.groups
            //     setCurrentActiveButton(AccountType.LedgerAccount - 1)
            //     handleMatchRegEx(AccountType.LedgerAccount, BuKrNr, AccountNr, AccountName)
            //     return
            // }
            if ((matching = e.value.match(regExLedger1))) {
                const {BuKrNr, AccountNr, AccountName} = matching.groups
                setCurrentActiveButton(AccountType.LedgerAccount)
                handleMatchRegEx(AccountType.LedgerAccount, BuKrNr, AccountNr, AccountName)
                return
            }
            if ((matching = e.value.match(regExLedger2))) {
                const {BuKrNr, AccountNr, AccountName} = matching.groups
                setCurrentActiveButton(AccountType.LedgerAccount)
                handleMatchRegEx(AccountType.LedgerAccount, BuKrNr, AccountNr, AccountName)
                return
            }
            if ((matching = e.value.match(regExLedgerName))) {
                const {BuKrNr, AccountNr, AccountName} = matching.groups
                setCurrentActiveButton(AccountType.LedgerAccount)
                handleMatchRegEx(AccountType.LedgerAccount, BuKrNr, AccountNr, AccountName)
                return
            }
            else {
                dropdownbox.current?.instance()?.blur()
            }
        }

    }, [accountsData, activeButton, multiselect, rangeMode, setFieldValue, field, name, setFieldTouched, handleMatchRegEx, allowedAccountTypes, allowedAccountMatching, onSelectionChanged, setLookupFieldValue])


    const onFocusOut = useCallback((e: any) => {
        if (open) {return}
        setInFocus(false)
    }, [open])

    const onFocusIn = useCallback(() => {
        setInFocus(true)
    }, [])

    const onOptionChanged = (e: any) => {
        if (e.name === 'opened') {
            setOpen(e.value)
        }
    }

    const displayValue = useMemo(() => {
        return existRow
            ? inFocus
                ? `${!rangeMode ? `${existRow?.BuKrNr}+` : ''}${existRow?.AccountNr}`
                : `${number.prefix}${!rangeMode ? `${existRow?.BuKrNr}+` : ''}${existRow?.AccountNr}${number.suffix}${separator.value}${info.prefix}${existRow?.AccountName}${info.suffix}`
            : rangeMode ? field.value : ''

    }, [inFocus, existRow, number, info, separator, rangeMode, field.value])

    const handleKeyUp = useCallback((e: any) => {
        if (e.event.key.length === 1 || e.event.key === 'Backspace') {
            sessionStorage.setItem('fieldValue', e.event.currentTarget.value)
        }
    },[])

    const onKeyDown = useCallback((e: any) => {
        if (e.event.key === ',' && !rangeWithComma) {
            e.event.preventDefault()
            return false
        }
    },[rangeWithComma])

    const ddButtons = useMemo<any>(() => ([
        { name: 'clear' },
        {
            location: 'after',
            name: 'dropDown',
        },
        {
            location: 'before',
            name: 'label',
            options: {
                text: label,
                stylingMode: 'text',
                hoverStateEnabled: false,
                focusStateEnabled: false,
                activeStateEnabled: false,
                elementAttr: {
                    class: 'label-inside-field'
                }
            }
        }
    ]), [label])

    return (
        <IxLookupProvider onFieldChanged={callback} onFieldValueNotFound={onFieldValueNotFound}>
            <div className='rb-item mb-1'>
                <div>
                    <label className='dx-field-label'>{label || 'Account'}</label>
                    {
                        isLoading
                            ? (
                                <div className='base-lookup--loader'>
                                    <LoadIndicator height={16} width={16} />
                                </div>
                            )
                            : (
                                <DropDownBox
                                    label={label}
                                    labelMode= 'floating'
                                    buttons={ddButtons}
                                    ref={dropdownbox}
                                    contentRender={contentRender}
                                    dropDownOptions={dropDownOptions}
                                    placeholder={t('global:label.select')}
                                    value={displayValue}
                                    showClearButton={field.value}
                                    onValueChanged={handleChange}
                                    className='dx-field-input'
                                    openOnFieldClick={openLookupOnClick}
                                    acceptCustomValue={true}
                                    onOptionChanged={onOptionChanged}
                                    onFocusIn={onFocusIn}
                                    onFocusOut={onFocusOut}
                                    readOnly={open}
                                    deferRendering={false}
                                    opened={open}
                                    inputAttr={{name: name}}
                                    onKeyUp={handleKeyUp}
                                    {...!multiselect && rangeMode ? {onKeyDown: onKeyDown} : {}}
                                />
                            )
                    }
                    <FormErrors meta={meta} />
                </div>

            </div>
        </IxLookupProvider>
    )
}

export const IxAccountLookup = AccountLookup
